import { ContactType } from "../models/Contact";

export type FeatureLevel = 0 | 1 | 2;

export const FEATURE_IDS = [
  "dashboard",
  "chat",
  "leads",
  "accounts",
  "vehicles",
  "parts",
  "orderManagement",
  "widget",
  "admin",
  "findLead",
] as const;

export type FeatureId = (typeof FEATURE_IDS)[number];

export type FeatureScopes = Record<FeatureId, FeatureLevel>;

export const FEATURE_CLAIM_PREFIX = "feat.";
/** Guid of a customer-defined CustomerRole, or "custom" when scopes were hand-edited. */
export const DASHBOARD_ROLE_CLAIM = "dashboardRoleId";

export const FEATURE_LABELS: Record<FeatureId, string> = {
  dashboard: "Dashboard",
  chat: "Team Chat",
  leads: "Leads",
  accounts: "Accounts",
  vehicles: "Vehicles",
  parts: "Parts",
  orderManagement: "Order Management",
  widget: "Widget",
  admin: "Admin",
  findLead: "Find Lead",
};

export function featureClaimKey(feature: FeatureId): string {
  return `${FEATURE_CLAIM_PREFIX}${feature}`;
}

export function emptyFeatureScopes(level: FeatureLevel = 0): FeatureScopes {
  return FEATURE_IDS.reduce((acc, id) => {
    acc[id] = level;
    return acc;
  }, {} as FeatureScopes);
}

export function allWriteScopes(): FeatureScopes {
  return emptyFeatureScopes(2);
}

export type DashboardRoleId =
  | "admin"
  | "staff"
  | "mechanic"
  | "yard"
  | "office"
  | "driver"
  | "exporter"
  | "custom";

export interface RoleTemplate {
  id: DashboardRoleId;
  label: string;
  contactType: ContactType;
  scopes: FeatureScopes;
}

const staffScopes = (): FeatureScopes => ({
  ...emptyFeatureScopes(0),
  dashboard: 2,
  chat: 2,
  leads: 2,
  accounts: 2,
  vehicles: 2,
  parts: 2,
  orderManagement: 2,
  widget: 2,
});

export const ROLE_TEMPLATES: RoleTemplate[] = [
  {
    id: "admin",
    label: "Admin",
    contactType: ContactType.Admin,
    scopes: allWriteScopes(),
  },
  {
    id: "staff",
    label: "Staff",
    contactType: ContactType.Staff,
    scopes: staffScopes(),
  },
  {
    id: "mechanic",
    label: "Mechanic",
    contactType: ContactType.Mechanic,
    scopes: staffScopes(),
  },
  {
    id: "yard",
    label: "Yard",
    contactType: ContactType.Staff,
    scopes: {
      ...emptyFeatureScopes(0),
      dashboard: 2,
      chat: 2,
      vehicles: 2,
      parts: 2,
    },
  },
  {
    id: "office",
    label: "Office",
    contactType: ContactType.Staff,
    scopes: {
      ...emptyFeatureScopes(0),
      dashboard: 2,
      chat: 2,
      leads: 2,
      accounts: 2,
      orderManagement: 2,
      widget: 2,
    },
  },
  {
    id: "driver",
    label: "Driver",
    contactType: ContactType.Driver,
    scopes: {
      ...emptyFeatureScopes(0),
      findLead: 2,
    },
  },
  {
    id: "exporter",
    label: "Exporter",
    contactType: ContactType.Exporter,
    scopes: {
      ...emptyFeatureScopes(0),
      parts: 2,
    },
  },
];

export function getRoleTemplate(id: DashboardRoleId): RoleTemplate | undefined {
  return ROLE_TEMPLATES.find((t) => t.id === id);
}

export interface RoleFlags {
  isAdmin: boolean;
  isStaff: boolean;
  isDriver: boolean;
  isMechanic: boolean;
  isExporter: boolean;
}

/** Flat claim map as it appears on the JWT / UserClaims. */
export type ClaimMap = Record<string, string | undefined>;

export function parseFeatureLevel(value: string | undefined): FeatureLevel {
  if (value === "1") return 1;
  if (value === "2") return 2;
  return 0;
}

export function hasExplicitFeatureClaims(claims: ClaimMap): boolean {
  return FEATURE_IDS.some((id) => {
    const value = claims[featureClaimKey(id)];
    return value !== undefined && value !== "";
  });
}

export function legacyFeatureScopes(flags: RoleFlags): FeatureScopes {
  if (flags.isAdmin) return allWriteScopes();
  if (flags.isDriver) {
    return { ...emptyFeatureScopes(0), findLead: 2 };
  }
  if (flags.isExporter) {
    return { ...emptyFeatureScopes(0), parts: 2 };
  }
  if (flags.isStaff || flags.isMechanic) {
    return staffScopes();
  }
  return emptyFeatureScopes(0);
}

export function parseFeatureScopesFromClaims(claims: ClaimMap): FeatureScopes {
  const scopes = emptyFeatureScopes(0);
  for (const id of FEATURE_IDS) {
    scopes[id] = parseFeatureLevel(claims[featureClaimKey(id)]);
  }
  return scopes;
}

/**
 * Resolve effective feature scopes from JWT/user claims.
 * Admins always get full write access. Users without any feat.* claims
 * fall back to the legacy role-flag mapping.
 */
export function resolveFeatureScopes(
  claims: ClaimMap,
  flags: RoleFlags
): FeatureScopes {
  if (flags.isAdmin) return allWriteScopes();
  if (hasExplicitFeatureClaims(claims)) {
    return parseFeatureScopesFromClaims(claims);
  }
  return legacyFeatureScopes(flags);
}

export function canAccessFeature(
  scopes: FeatureScopes | undefined,
  feature: FeatureId,
  minLevel: FeatureLevel = 1
): boolean {
  if (!scopes) return false;
  return scopes[feature] >= minLevel;
}

/** Map sidebar group label (English source key) or path to a feature id. */
export function featureForNavGroup(group: string | undefined, to: string): FeatureId | null {
  if (!group) {
    if (to === "/" || to === "") return "dashboard";
    if (to === "chat") return "chat";
    if (to === "find-lead") return "findLead";
    if (to === "exports") return "parts";
    return null;
  }

  switch (group) {
    case "Leads":
      return "leads";
    case "Accounts":
      return "accounts";
    case "Vehicles":
      return "vehicles";
    case "Parts":
      return "parts";
    case "Order Management":
      return "orderManagement";
    case "Widget":
      return "widget";
    case "Admin":
      // Find Lead sits in Admin nav but uses its own scope.
      if (to === "find-lead") return "findLead";
      return "admin";
    case "Debug":
      return "admin";
    default:
      return null;
  }
}

/**
 * Longest-prefix match of path segment(s) to a feature.
 * Path should be without leading slash, e.g. "leads/abc" or "".
 */
export const ROUTE_FEATURE_MAP: { prefix: string; feature: FeatureId }[] = [
  { prefix: "", feature: "dashboard" },
  { prefix: "chat", feature: "chat" },
  { prefix: "quotes", feature: "leads" },
  { prefix: "bookings", feature: "leads" },
  { prefix: "leads", feature: "leads" },
  { prefix: "vehicles-collected", feature: "leads" },
  { prefix: "calendar", feature: "leads" },
  { prefix: "trip-manager", feature: "leads" },
  { prefix: "old-trip-manager", feature: "leads" },
  { prefix: "drop-offs", feature: "leads" },
  { prefix: "payments", feature: "leads" },
  { prefix: "schedule", feature: "leads" },
  { prefix: "accounts", feature: "accounts" },
  { prefix: "invoices", feature: "accounts" },
  { prefix: "vehicles", feature: "vehicles" },
  { prefix: "vehicle-board", feature: "vehicles" },
  { prefix: "parts-manager", feature: "vehicles" },
  { prefix: "depollution", feature: "vehicles" },
  { prefix: "triage", feature: "vehicles" },
  { prefix: "jobs", feature: "vehicles" },
  { prefix: "production-planner", feature: "vehicles" },
  { prefix: "production-dashboard", feature: "vehicles" },
  { prefix: "station-manager", feature: "vehicles" },
  { prefix: "work-orders", feature: "parts" },
  { prefix: "exports", feature: "parts" },
  { prefix: "awaiting-arrival", feature: "parts" },
  { prefix: "parts-checker", feature: "parts" },
  { prefix: "stock", feature: "parts" },
  { prefix: "core", feature: "parts" },
  { prefix: "stock-qc", feature: "parts" },
  { prefix: "ebay-unlisted", feature: "orderManagement" },
  { prefix: "recently-listed", feature: "orderManagement" },
  { prefix: "ebay-listings", feature: "orderManagement" },
  { prefix: "ebay-orders", feature: "orderManagement" },
  { prefix: "ebay-returns", feature: "orderManagement" },
  { prefix: "widget-dashboard", feature: "widget" },
  { prefix: "widget-calculator", feature: "widget" },
  { prefix: "widget-postcodes", feature: "widget" },
  { prefix: "widget-suppliers", feature: "widget" },
  { prefix: "sellavehicle", feature: "widget" },
  { prefix: "abandoned-quotes", feature: "widget" },
  { prefix: "widget-config", feature: "widget" },
  { prefix: "admin", feature: "admin" },
  { prefix: "parts-editor", feature: "admin" },
  { prefix: "ebay-messages", feature: "admin" },
  { prefix: "category-editor", feature: "admin" },
  { prefix: "category-images", feature: "admin" },
  { prefix: "reports", feature: "admin" },
  { prefix: "performance", feature: "admin" },
  // "tickets" is deliberately unmapped — support tickets stay open to everyone.
  { prefix: "driver-tracking", feature: "admin" },
  { prefix: "test", feature: "admin" },
  { prefix: "debug", feature: "admin" },
  { prefix: "find-lead", feature: "findLead" },
  { prefix: "driver", feature: "findLead" },
  { prefix: "settings", feature: "admin" },
  { prefix: "contacts", feature: "accounts" },
  { prefix: "search", feature: "dashboard" },
];

export function featureForRoutePath(pathname: string): FeatureId | null {
  const normalised = pathname.replace(/^\//, "").replace(/\/$/, "");
  // Sort by longest prefix so "ebay-orders" wins over "" etc.
  const sorted = [...ROUTE_FEATURE_MAP].sort(
    (a, b) => b.prefix.length - a.prefix.length
  );
  for (const entry of sorted) {
    if (entry.prefix === "") {
      if (normalised === "") return entry.feature;
      continue;
    }
    if (
      normalised === entry.prefix ||
      normalised.startsWith(`${entry.prefix}/`)
    ) {
      return entry.feature;
    }
  }
  return null;
}

export function contactTypeToRoleFlags(type: ContactType): RoleFlags {
  return {
    isAdmin: type === ContactType.Admin,
    isStaff: type === ContactType.Staff,
    isDriver: type === ContactType.Driver,
    isMechanic: type === ContactType.Mechanic,
    isExporter: type === ContactType.Exporter,
  };
}

export function roleFlagsToContactType(flags: RoleFlags): ContactType | undefined {
  if (flags.isAdmin) return ContactType.Admin;
  if (flags.isDriver) return ContactType.Driver;
  if (flags.isStaff) return ContactType.Staff;
  if (flags.isMechanic) return ContactType.Mechanic;
  if (flags.isExporter) return ContactType.Exporter;
  return undefined;
}

export function roleFlagsToClaims(flags: RoleFlags): Record<string, string> {
  return {
    isAdmin: flags.isAdmin ? "true" : "false",
    isStaff: flags.isStaff ? "true" : "false",
    isDriver: flags.isDriver ? "true" : "false",
    isMechanic: flags.isMechanic ? "true" : "false",
    isExporter: flags.isExporter ? "true" : "false",
  };
}

export function featureScopesToClaims(scopes: FeatureScopes): Record<string, string> {
  return FEATURE_IDS.reduce((acc, id) => {
    acc[featureClaimKey(id)] = String(scopes[id]);
    return acc;
  }, {} as Record<string, string>);
}

export function buildStaffClaims(
  flags: RoleFlags,
  scopes: FeatureScopes,
  dashboardRoleId: string
): Record<string, string> {
  return {
    ...roleFlagsToClaims(flags),
    ...featureScopesToClaims(scopes),
    [DASHBOARD_ROLE_CLAIM]: dashboardRoleId,
  };
}

export function scopesFromRecord(
  record: Record<string, number> | undefined
): FeatureScopes {
  const scopes = emptyFeatureScopes(0);
  if (!record) return scopes;
  for (const id of FEATURE_IDS) {
    const level = record[id];
    if (level === 1 || level === 2) scopes[id] = level;
  }
  return scopes;
}

export function scopesMatch(
  a: FeatureScopes,
  b: FeatureScopes
): boolean {
  return FEATURE_IDS.every((id) => a[id] === b[id]);
}

/** Returns the customer role id from claims, or "custom". */
export function detectAssignedRoleId(claimMap: ClaimMap): string {
  const stored = claimMap[DASHBOARD_ROLE_CLAIM];
  if (!stored || stored === "custom") return "custom";
  return stored;
}

export function parseScopesFromClaimMap(claimMap: ClaimMap): FeatureScopes {
  return FEATURE_IDS.reduce((acc, id) => {
    acc[id] = parseFeatureLevel(claimMap[featureClaimKey(id)]);
    return acc;
  }, emptyFeatureScopes(0));
}

export function userClaimsToMap(
  userClaims: { type: string; value: string }[] | undefined
): ClaimMap {
  const map: ClaimMap = {};
  (userClaims ?? []).forEach((c) => {
    map[c.type] = c.value;
  });
  return map;
}

export function claimMapToRoleFlags(claims: ClaimMap): RoleFlags {
  return {
    isAdmin: claims.isAdmin === "true",
    isStaff: claims.isStaff === "true",
    isDriver: claims.isDriver === "true",
    isMechanic: claims.isMechanic === "true",
    isExporter: claims.isExporter === "true",
  };
}

/** Index signature friendly view of a decoded JWT for resolveFeatureScopes. */
export function tokenPayloadToClaimMap(
  payload: Record<string, unknown>
): ClaimMap {
  const map: ClaimMap = {};
  for (const [key, value] of Object.entries(payload)) {
    if (typeof value === "string") map[key] = value;
    else if (typeof value === "boolean" || typeof value === "number") {
      map[key] = String(value);
    }
  }
  return map;
}
